/**
 * Page 4 — Supervisors.
 *
 * A team is the supervisor's whole tree — every level below them, people with no
 * claims included at full salary, and the supervisor's own labor and claims. See
 * measures/teams.ts. Teams overlap, so the cards do not add up to the org total.
 *
 * Supervisors rank against supervisors with their own job title — a Medicare
 * Supervisor against Medicare Supervisors only (2026-10-02). MEASURE-RULES §10.7.
 */
import { useCallback, useMemo } from 'react'
import { useAppData } from '@/data/AppData'
import { chainOnlySupervisors } from '@/data/selectors'
import { useFilters, ALL } from '@/data/filters'
import { StatStrip, Stat, Select, SupervisorTitle } from '@/components/Bits'
import { SidePanel } from '@/components/SidePanel'
import { TeamDetail } from '@/components/TeamDetail'
import { PopOut } from '@/components/PopOut'
import { money2, int, dec, monthSpan } from '@/format'
import { rankShort } from '@/measures/rankText'
import { defineTeam } from '@/measures/definitions'
import { useUrlState, maybeText, list, oneOf } from '@/ui/urlState'
import {
  buildTeams,
  teamRanking,
  availableTeamMetrics,
  TEAM_METRICS,
  availableSupervisorViewMetrics,
  pooledCollection,
  pooledCostPerClaim,
  pooledOpenWorked,
  pooledZeroTouch,
  teamGroup,
  supervisorCardFilter,
  byOverallRank,
  type Team,
  type TeamMetric,
} from '@/measures/teams'


export function Supervisors() {
  const { derived, scope, openPerson, coverage, label } = useAppData()
  const { filters } = useFilters()
  /** The months every rank on the page covers, for its hover. */
  const period = monthSpan(derived.months)
  // Kept for Back: the list's settings and the sidebar's depth in the URL, whose team is
  // open and which sub-teams in the history entry, since they are names. See
  // ui/urlState.ts. The keys are My team's metrics' own, so the sidebar keeps its state
  // the way that page does.
  const [orderBy, setOrderBy] = useUrlState(
    'order',
    'cpc',
    oneOf(['cpc', 'rank', 'excess', 'span', 'cpm'] as const, 'cpc'),
  )
  /**
   * The team open in the sidebar, by supervisor. `team` is the key the page's old
   * pop-up used, so a history entry saved with it open reopens as the sidebar.
   */
  const [panelTeam, setPanelTeam] = useUrlState('team', null, maybeText, { personal: true })
  const [panelDepth, setPanelDepth] = useUrlState(
    'show',
    'tree',
    oneOf(['tree', 'direct'] as const, 'tree'),
  )
  const [panelOpen, setPanelOpen] = useUrlState('open', [] as string[], list, { personal: true })
  const openPanel = (t: Team) => {
    setPanelOpen([])
    setPanelTeam(t.supervisor)
  }
  const closePanel = useCallback(() => {
    setPanelTeam(null)
    setPanelOpen([])
  }, [setPanelTeam, setPanelOpen])
  /** The rows this bundle can populate. See data/coverage.ts. */
  const rows = useMemo(() => availableTeamMetrics(coverage), [coverage])


  /**
   * Every team carrying claims — the pool cohorts are drawn from, whatever the
   * Supervisor, Job title or Employee filter says. Those three choose whose cards show,
   * never what a team is or who it ranks against: built from `supervisorPool`, which
   * leaves Job title and Employee off. Built from the narrowed pool, picking a title
   * cut every team down to the people holding it (review #21, 2026-10-02).
   *
   * The Management toggle decides whose labor and claims are counted (`teamCounts`),
   * never who has a card: a card is a team that carries claims with everyone counted.
   * With management off, a supervisor whose claims were all their own keeps a card
   * reading "no data" rather than vanishing (review #106, 2026-10-06).
   *
   * A supervisor who only repeats the one person below them gets no card
   * (`chainOnlySupervisors`): under View as, or any department-scoped login, every
   * executive above the department would otherwise read as the scope again.
   */
  const allTeams = useMemo(() => {
    const skip = chainOnlySupervisors(scope.supervisorPool)
    return buildTeams(scope.supervisorPool, derived.byName, 'tree', scope.teamCounts).filter(
      (t) => !skip.has(t.supervisor) && t.everyone.some((p) => p.claims > 0),
    )
  }, [scope.supervisorPool, derived.byName, scope.teamCounts])
  /**
   * Only a team that has a card opens: a name left in an old history entry that no
   * longer matches one, or not under these filters, is ignored rather than opening an
   * empty sidebar.
   */
  const panelFor =
    panelTeam !== null && allTeams.some((t) => t.supervisor === panelTeam) ? panelTeam : null

  /** Under the Supervisor filter: that supervisor and everyone in their tree who has a team. */
  const shown = useMemo(() => {
    if (filters.supervisor === ALL) return null
    const out = new Set([filters.supervisor])
    for (const p of scope.supervisorPool) {
      const i = p.supervisors.indexOf(filters.supervisor)
      for (const s of p.supervisors.slice(0, Math.max(i, 0))) out.add(s)
    }
    return out
  }, [filters.supervisor, scope.supervisorPool])

  /** Job title picks supervisors by their own title; Employee, the teams that person is on. */
  const picked = useMemo(
    () => supervisorCardFilter(allTeams, filters.jobTitle, filters.employee, ALL),
    [allTeams, filters.jobTitle, filters.employee],
  )

  // Every supervisor whose team carries claims gets a card. The minimum-people slider
  // was removed at the business owners' request (2026-09-23): with teams as whole
  // trees a card is no longer a two-person sample, and a small team still ranks only
  // against its own job title, never below the cohort floor.
  const teams = useMemo(
    () => allTeams.filter((t) => (shown === null || shown.has(t.supervisor)) && picked(t)),
    [allTeams, shown, picked],
  )

  /**
   * Ranks come off the same teams built from people active in the period, whatever the Employee
   * status control shows: people who had left count in a team's figures on screen,
   * and in its rank only for the months they were active. See `teamRanking`.
   */
  const ranking = useMemo(
    () => teamRanking(scope.supervisorRankPool, derived.byName, 'tree', scope.teamCounts),
    [scope.supervisorRankPool, derived.byName, scope.teamCounts],
  )
  const cohortOf = ranking.cohort
  const rankOf = ranking.rank
  /** The hover note when people who have left move the figure shown off the one ranked. */
  const rankedOn = (t: Team, row: TeamMetric, r: { value: number }) =>
    row.fmt(row.get(t)) === row.fmt(r.value)
      ? ''
      : ` — ranked on the months people were active, ${row.fmt(r.value)}`


  /** The Ledger's wording for a card row, where it differs from the metric's own label. */
  const CARD_LABEL: Record<string, string> = {
    perEmployeePerMonth: 'Billed claims / employee / mo',
    costPerClaim: 'Team cost / claim',
    openWorked: 'Open worked in month',
  }

  /** The Ledger's six card rows, in its order. */
  const CARD_ROWS = useMemo(
    () =>
      ['perEmployeePerMonth', 'costPerClaim', 'collection', 'closedPct', 'zeroTouch', 'openWorked']
        .map((k) => rows.find((r) => r.key === k))
        .filter((r): r is (typeof TEAM_METRICS)[number] => r !== undefined),
    [rows],
  )

  /**
   * The average each bar is drawn against — across every team shown: cost pooled,
   * open-worked pooled on its own open pile, 0% touch pooled on its own touch base,
   * collection pooled on charge dollars (2026-09-30), the other rates weighted by
   * billed claims, volumes a plain mean.
   *
   * Open-worked departs from the Ledger here on purpose. Hers weights this tick by
   * billed claims like the other rates, though her own team figure — and the rule of
   * record — weight it by open claims. Billed-weighting read 9.8% against a pooled
   * 8.7% on the 2026-09-23 bundle's default view.
   *
   * 0% touch departs from the Ledger too. Hers billed-weights both the team figure and
   * this tick; the app's team figure pools on the touch base, and a tick weighted
   * differently from the bars it sits on compares unlike things.
   *
   * The four pooled figures count each PERSON once. Teams are whole trees and
   * overlap, so pooling team totals would count someone again for every level above
   * them.
   */
  const pooledCost = useMemo(() => pooledCostPerClaim(teams), [teams])

  const overallAvg = useMemo(() => {
    const out = new Map<string, number | null>()
    for (const row of CARD_ROWS) {
      if (row.key === 'costPerClaim') {
        out.set(row.key, pooledCost)
      } else if (row.key === 'openWorked') {
        out.set(row.key, pooledOpenWorked(teams))
      } else if (row.key === 'zeroTouch') {
        out.set(row.key, pooledZeroTouch(teams))
      } else if (row.key === 'collection') {
        out.set(row.key, pooledCollection(teams))
      } else if (row.key === 'perEmployeePerMonth') {
        const v = teams.map(row.get).filter((x): x is number => x !== null)
        out.set(row.key, v.length ? v.reduce((a, b) => a + b, 0) / v.length : null)
      } else {
        let num = 0
        let den = 0
        for (const t of teams) {
          const v = row.get(t)
          if (v === null) continue
          num += v * t.billed
          den += t.billed
        }
        out.set(row.key, den === 0 ? null : num / den)
      }
    }
    return out
  }, [teams, CARD_ROWS, pooledCost])

  /** One overall place per supervisor, on the same seven measures as the Supervisor view. */
  const rankMetrics = useMemo(() => availableSupervisorViewMetrics(coverage), [coverage])
  const overallRank = (t: Team) => ranking.overall(t, rankMetrics)

  // Before the card filters, or "vs org rate" would read "vs this team's rate".
  const orgRate = useMemo(() => {
    const counted = scope.supervisorPool.filter(scope.teamCounts)
    const billed = counted.reduce((a, p) => a + p.claims, 0)
    const labor = counted.reduce((a, p) => a + p.claimLabor, 0)
    return billed === 0 ? null : labor / billed
  }, [scope.supervisorPool, scope.teamCounts])

  /**
   * The Ledger's four orders, and Rank (Shayna, review #111, 2026-10-06: "can we add rank
   * here?"). Excess is dollars over the org rate across the team's claims.
   *
   * Rank is the card's own badge, best first; see `byOverallRank`.
   */
  const ordered = useMemo(() => {
    const excess = (t: Team) =>
      t.costPerClaim === null || orgRate === null ? -Infinity : (t.costPerClaim - orgRate) * t.billed
    const last = (v: number | null, dir: 1 | -1) => (v === null ? Infinity : dir * v)
    const byRank = byOverallRank(teams, (t) => ranking.overall(t, rankMetrics))
    return [...teams].sort((a, b) =>
      orderBy === 'rank'
        ? byRank(a, b)
        : orderBy === 'cpc'
          ? last(a.costPerClaim, -1) - last(b.costPerClaim, -1)
          : orderBy === 'excess'
            ? excess(b) - excess(a)
            : orderBy === 'span'
              ? b.members.length - a.members.length
              : last(a.perEmployeePerMonth, 1) - last(b.perEmployeePerMonth, 1),
    )
  }, [teams, orderBy, orgRate, ranking, rankMetrics])

  const summary = useMemo(() => {
    const per = teams.map((t) => t.perEmployeePerMonth).filter((v): v is number => v !== null)
    const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
    return {
      count: teams.length,
      ranked: teams.filter((t) => overallRank(t)?.rank != null).length,
      // Pooled, not a mean of team rates — the same figure as the tick on every cost bar.
      avgCost: pooledCost,
      avgPer: avg(per),
      avgReports: avg(teams.map((t) => t.direct.length)),
    }
  }, [teams, ranking, rankMetrics, pooledCost])

  return (
    <>
      <h1 className="page-title">Supervisor scorecard</h1>
      <p className="body-note" style={{ margin: '6px 0 0' }}>
        Each team is everyone below the supervisor at every level, plus the supervisor,
        so teams overlap and the cards don’t add up to the org total.
      </p>

      <div style={{ marginTop: 14 }}>
        <StatStrip cols={4}>
          <Stat
            label="Supervisors compared"
            value={int(summary.count)}
            hero
            note={`${int(summary.ranked)} ranked against 4+ with their job title`}
          />
          <Stat
            label="Average team cost / claim"
            value={money2(summary.avgCost)}
            note="everyone on these cards, once each"
          />
          <Stat
            label="Avg claims / employee / month"
            value={int(summary.avgPer)}
            note="billed claims per person per month"
          />
          <Stat
            label="Average direct reports"
            value={dec(summary.avgReports, 1)}
            note="one level down"
          />
        </StatStrip>
      </div>

      <PopOut title="Supervisors compared" note="one card per supervisor, team rolled up">
        {({ button }) => (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 14px', alignItems: 'flex-end', marginTop: 18 }}>
              <div style={{ minWidth: 240 }}>
                <Select
                  label="Order cards by"
                  lead
                  value={orderBy}
                  options={[
                    { value: 'cpc', label: 'Highest team cost per claim' },
                    { value: 'rank', label: 'Rank (best first)' },
                    { value: 'excess', label: 'Most dollars over the org rate' },
                    { value: 'span', label: 'Largest team' },
                    { value: 'cpm', label: 'Fewest claims per employee per month' },
                  ]}
                  onChange={(v) => setOrderBy(v as typeof orderBy)}
                />
              </div>
              <span style={{ marginLeft: 'auto' }}>{button}</span>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 368px), 1fr))',
                gap: 14,
                marginTop: 18,
              }}
            >
              {ordered.map((t) => {
                const cohort = cohortOf(t)
                const overall = overallRank(t)
                const rankCls =
                  overall === null || overall.rank === null
                    ? 'none'
                    : overall.rank <= Math.ceil(overall.of / 3)
                      ? 'good'
                      : overall.rank > overall.of - Math.ceil(overall.of / 3)
                        ? 'bad'
                        : 'mid'
                const rel =
                  t.costPerClaim !== null && summary.avgCost ? t.costPerClaim / summary.avgCost : 1
                const tone = rel > 1.15 ? 'crit' : rel < 0.85 ? 'good' : 'neut'
                const cohortName = teamGroup(t)
                /** Who this card is ranked against, said on the rank itself (Shayna, 2026-10-02). */
                const overallTitle = ranking.explain(t, null, rankMetrics, period)

                return (
                  <div
                    key={t.supervisor}
                    className={`supcard ${tone}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => openPanel(t)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') openPanel(t)
                    }}
                  >
                    <h4>
                      <span>
                        {t.supervisor}
                        <br />
                        <SupervisorTitle t={t} />
                      </span>
                      {overall && overall.rank !== null ? (
                        <b className={`suprank ${rankCls}`} title={overallTitle}>
                          <em>rank</em>
                          {overall.rank}
                          <span>of {overall.of}</span>
                        </b>
                      ) : (
                        <b className="suprank none" title={overallTitle}>
                          {overall && rankShort(overall) === 'no data' ? 'no data' : 'unranked'}
                        </b>
                      )}
                    </h4>
                    <div style={{ marginTop: 5 }}>
                      <span className="pts-chip neut" title={overallTitle}>
                        {ranking.unranked(t)
                          ? `${ranking.unranked(t)} — unranked`
                          : overall && overall.of - overall.noData >= overall.floor
                            ? `ranked among ${cohort.length} ${cohortName}`
                            : cohort.length <= 1
                              ? 'only one with this job title — unranked'
                              : `only ${cohort.length} ${cohortName} — unranked`}
                      </span>
                    </div>

                    <div className="supcard-hero">
                      <b>{money2(t.costPerClaim)}</b>
                      {t.costPerClaim !== null && (
                        <span className={`pts-chip ${tone}`}>
                          {rel >= 1 ? '+' : ''}
                          {Math.round((rel - 1) * 100)}% vs avg
                        </span>
                      )}
                      <span className="hl">team cost / claim</span>
                    </div>

                    <div className="duo" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                      <div title={`${t.direct.length} people report to ${t.supervisor} directly`}>
                        <span className="k">Direct reports</span>
                        <b>{t.direct.length}</b>
                      </div>
                      <div title="Everyone below them at every level, plus them where they are in this data">
                        <span className="k">Whole team</span>
                        <b>{t.members.length}</b>
                      </div>
                      <div>
                        <span className="k">Carrying claims</span>
                        <b>{t.carrying.length}</b>
                      </div>
                    </div>

                    <div className="srow head">
                      <span>Metric</span>
                      <span>Value</span>
                      <span style={{ textAlign: 'center' }}>avg</span>
                      <span style={{ textAlign: 'right' }}>rank</span>
                    </div>
                    {CARD_ROWS.map((row) => {
                      const v = row.get(t)
                      const avg = overallAvg.get(row.key) ?? null
                      const r = rankOf(t, row)
                      if (v === null)
                        return (
                          <div key={row.key} className="srow">
                            <span className="k" title={defineTeam(row.key)?.text}>
                              {label(CARD_LABEL[row.key] ?? row.label)}
                            </span>
                            <span className="v">—</span>
                            <span className="g" />
                            <span className="q dim" title={`${row.label}: ${ranking.explain(t, row, rankMetrics, period)}`}>
                              {ranking.unranked(t) ? '—' : 'no data'}
                            </span>
                          </div>
                        )
                      const cap = Math.max(v, avg ?? 0) * 1.3 || 1
                      const w = Math.max(2, Math.min(100, (v / cap) * 100))
                      const a = Math.max(0, Math.min(100, ((avg ?? 0) / cap) * 100))
                      const better = avg === null ? true : row.lowerIsBetter ? v <= avg : v >= avg
                      const pctile = r ? 1 - r.pctile : 1
                      return (
                        <div key={row.key} className="srow">
                          {/* A plain hover title, not an "i": 146 cards × 7 rows of icons would
                              bury the figures. The sidebar's table carries the full definitions. */}
                          <span className="k" title={defineTeam(row.key)?.text}>
                            {label(CARD_LABEL[row.key] ?? row.label)}
                          </span>
                          <span className="v">{row.fmt(v)}</span>
                          <span
                            className="g"
                            title={`${row.label} ${row.fmt(v)} vs average ${row.fmt(avg)}`}
                          >
                            <i style={{ width: `${w}%`, background: better ? 'var(--good)' : 'var(--crit)' }} />
                            <span className="avg" style={{ left: `${a}%` }} />
                          </span>
                          {r ? (
                            <span
                              className="q"
                              style={{
                                color:
                                  pctile >= 2 / 3 ? 'var(--good)' : pctile >= 1 / 3 ? 'var(--s1)' : 'var(--crit)',
                              }}
                              title={`${row.label} ${row.fmt(v)} · ${ranking.explain(t, row, rankMetrics, period)}${rankedOn(t, row, r)}`}
                            >
                              {r.rank}
                              <span className="of">/{r.of}</span>
                            </span>
                          ) : (
                            <span
                              className="q dim"
                              title={`${row.label} ${row.fmt(v)} · ${ranking.explain(t, row, rankMetrics, period)}`}
                            >
                              —
                            </span>
                          )}
                        </div>
                      )
                    })}

                    {/*
                      The footer's "N billed claims · $X claim labor · +$Y vs org rate" line was
                      taken off in Shayna's 2026-10-02 review (#20). What is left says how to
                      read the card's own rate and where the card leads.
                    */}
                    <p className="supcard-foot">
                      {!t.selfInData && (
                        <>
                          <span title="Their team rolls up; their own labor is not in this data (outside the RCM population, or outside your access).">
                            Own salary not included: not in this data
                          </span>
                          <br />
                        </>
                      )}
                      {t.carrying.length === 0 && (
                        <>
                          <span title="Include management is off, so the team's figures leave out supervisors' and managers' own labor and claims. Every billed claim on this team is management's own.">
                            No data with management left out: the team’s claims are all management’s own
                          </span>
                          <br />
                        </>
                      )}
                      <span className="opencue">Open the team →</span>
                    </p>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </PopOut>

      {teams.length === 0 && (
        <p className="empty-note">
          {filters.jobTitle.length > 0
            ? 'No supervisor with the chosen job title has a team carrying claims. On this page Job title picks supervisors by their own title.'
            : 'No supervisor has a team carrying claims under these filters.'}
        </p>
      )}

      {/*
        A card opens the same team detail as My team's metrics, in the same sidebar a
        person opens in — the page's own pop-up is gone (2026-09-25 review).
      */}
      {panelFor && (
        <SidePanel onClose={closePanel} label={`Team of ${panelFor}`}>
          <TeamDetail
            supervisor={panelFor}
            depth={panelDepth}
            onDepth={setPanelDepth}
            openList={panelOpen}
            onOpenList={setPanelOpen}
            pool={scope.supervisorPool}
            rankPool={scope.supervisorRankPool}
            counts={scope.teamCounts}
            onOpenPerson={(name) => {
              const sup = panelFor
              const opened = panelOpen
              setPanelTeam(null)
              openPerson(name, {
                label: sup,
                onBack: () => {
                  setPanelTeam(sup)
                  setPanelOpen(opened)
                },
              })
            }}
          />
        </SidePanel>
      )}
    </>
  )
}
